import { cs } from '../../../lib/currency';
import { useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Label } from '../../ui/label';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { Checkbox } from '../../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { RefreshCw, AlertTriangle, DollarSign } from 'lucide-react';
import { formatTime } from './utils';
import EquipmentPicker from '../EquipmentPicker';

/**
 * Edit / Reschedule modal — single entry point for any change to a lane
 * booking: lane, date, time, duration, equipment add-ons, price override
 * and admin notes. The "Notify customer" checkbox controls whether the
 * customer receives a change-notification email.
 *
 * Per product spec (Feb 2026):
 *  - 2d → all four edit dimensions exposed
 *  - 3b → live price-delta surfaced for paid bookings; admin handles
 *    additional charges / partial refunds via existing payment / refund
 *    flows on the Booking Details modal
 *  - 4b → equipment availability is validated by the backend; admin can
 *    tick "Override conflicts" to force-save when needed
 *  - 5b → notify checkbox defaults to ON
 */
export const RescheduleModal = ({
  open,
  onOpenChange,
  form,
  setForm,
  booking,
  lanes,
  onSubmit,
}) => {
  // Live-derived end time for the equipment picker so availability lookups
  // reflect the duration the admin is currently editing.
  const derivedEndTime = useMemo(() => {
    if (!form?.start_time || !form?.duration) return form?.start_time || '00:00';
    const [h, m] = String(form.start_time).split(':').map(Number);
    const total = (Number.isFinite(h) ? h : 0) * 60
      + (Number.isFinite(m) ? m : 0)
      + parseInt(form.duration || '30', 10);
    return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
  }, [form?.start_time, form?.duration]);

  // Computed price preview — shown so the admin sees the ramifications of
  // their edits before clicking save.
  const equipmentTotal = useMemo(() => {
    return (form?.equipment_rentals || [])
      .reduce((sum, l) => sum + Number(l?.subtotal || 0), 0);
  }, [form?.equipment_rentals]);

  const overrideActive = form?.price_override_enabled
    && form?.price_override !== ''
    && form?.price_override !== null
    && form?.price_override !== undefined;
  const overridePrice = overrideActive ? Number(form.price_override) : null;

  const originalPrice = Number(booking?.price || 0);
  const previewPrice = overrideActive
    ? overridePrice
    // Lane base price unknown client-side until backend recomputes — fall
    // back to original lane share (booking.price minus original equipment).
    : (Number(booking?.price || 0) - Number(booking?.equipment_total || 0)) + equipmentTotal;
  const delta = (Number.isFinite(previewPrice) ? previewPrice : 0) - originalPrice;
  const isPaid = booking?.payment_status === 'paid';

  return (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="sm:max-w-lg max-h-[92vh] overflow-y-auto" data-testid="edit-reschedule-modal">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <RefreshCw className="h-5 w-5 text-blue-500" />
          Edit / Reschedule Booking
        </DialogTitle>
      </DialogHeader>
      <div className="space-y-4 py-2">
        {booking && (
          <div className="p-3 bg-slate-50 rounded-lg text-sm">
            <p><strong>Customer:</strong> {booking.customer_name}</p>
            <p><strong>Current:</strong> {booking.lane_name} on {booking.date}</p>
            <p><strong>Time:</strong> {formatTime(booking.start_time)} - {formatTime(booking.end_time)}</p>
            <p><strong>Original price:</strong> {cs()}{originalPrice.toFixed(2)}{isPaid ? ' (paid)' : ''}</p>
          </div>
        )}
        <div className="space-y-2">
          <Label>Lane</Label>
          <Select value={form.lane_id} onValueChange={(v) => setForm({ ...form, lane_id: v })}>
            <SelectTrigger data-testid="reschedule-lane-select"><SelectValue placeholder="Select lane" /></SelectTrigger>
            <SelectContent>
              {lanes.map((lane) => <SelectItem key={lane.id} value={lane.id}>{lane.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Date</Label>
            <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="reschedule-date" />
          </div>
          <div className="space-y-2">
            <Label>Start Time</Label>
            <Input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} data-testid="reschedule-time" />
          </div>
        </div>
        <div className="space-y-2">
          <Label>Duration</Label>
          <Select value={form.duration} onValueChange={(v) => setForm({ ...form, duration: v })}>
            <SelectTrigger data-testid="reschedule-duration-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="30">30 min</SelectItem>
              <SelectItem value="60">1 hour</SelectItem>
              <SelectItem value="90">1.5 hours</SelectItem>
              <SelectItem value="120">2 hours</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Equipment rentals — pre-loaded from existing booking */}
        <div className="border-t border-slate-100 pt-3">
          <EquipmentPicker
            date={form.date}
            startTime={form.start_time}
            endTime={derivedEndTime}
            excludeBookingId={booking?.id}
            value={form.equipment_rentals || []}
            onChange={(lines) => setForm({ ...form, equipment_rentals: lines })}
          />
        </div>

        {/* Manual price override */}
        <div className="border-t border-slate-100 pt-3 space-y-2">
          <div className="flex items-center gap-2">
            <Checkbox
              id="price_override_enabled"
              checked={!!form.price_override_enabled}
              onCheckedChange={(checked) => setForm({
                ...form,
                price_override_enabled: !!checked,
                price_override: checked ? (form.price_override || originalPrice.toFixed(2)) : '',
              })}
              data-testid="price-override-toggle"
            />
            <Label htmlFor="price_override_enabled" className="text-sm cursor-pointer flex items-center gap-1">
              <DollarSign className="h-3.5 w-3.5" />
              Manual price override
            </Label>
          </div>
          {form.price_override_enabled && (
            <Input
              type="number"
              step="0.01"
              min="0"
              value={form.price_override ?? ''}
              onChange={(e) => setForm({ ...form, price_override: e.target.value })}
              placeholder="Total price (USD)"
              data-testid="price-override-input"
            />
          )}
          <div className="text-xs text-slate-500">
            New total preview: <span className="font-semibold text-slate-900">{cs()}{Number(previewPrice || 0).toFixed(2)}</span>
            {Math.abs(delta) > 0.005 && (
              <span className={`ml-2 ${delta > 0 ? 'text-amber-700' : 'text-emerald-700'}`}>
                ({delta > 0 ? '+' : ''}{cs()}{delta.toFixed(2)} vs original)
              </span>
            )}
          </div>
          {isPaid && Math.abs(delta) > 0.005 && (
            <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2" data-testid="paid-delta-warning">
              <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span>
                This booking is already paid. After saving, use the
                Booking Details modal to {delta > 0 ? 'send a payment link for the extra amount' : 'issue a partial refund'}.
              </span>
            </div>
          )}
        </div>

        <div className="space-y-2">
          <Label>Admin Notes</Label>
          <Textarea
            value={form.notes || ''}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            placeholder="Internal notes about this booking…"
            rows={2}
            data-testid="reschedule-notes"
          />
        </div>

        <div className="space-y-2">
          <Label>Reason for Change (Optional)</Label>
          <Textarea
            value={form.reason}
            onChange={(e) => setForm({ ...form, reason: e.target.value })}
            placeholder="e.g., Customer requested, lane maintenance…"
            rows={2}
            data-testid="reschedule-reason"
          />
        </div>

        <div className="flex items-center gap-2">
          <Checkbox
            id="force_override"
            checked={!!form.force_override}
            onCheckedChange={(checked) => setForm({ ...form, force_override: !!checked })}
            data-testid="force-override-toggle"
          />
          <Label htmlFor="force_override" className="text-sm cursor-pointer flex items-center gap-1">
            <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
            Override slot / equipment conflicts
          </Label>
        </div>

        <div className="flex items-center gap-2">
          <Checkbox
            id="notify_customer"
            checked={form.notify_customer}
            onCheckedChange={(checked) => setForm({ ...form, notify_customer: checked })}
            data-testid="reschedule-notify"
          />
          <Label htmlFor="notify_customer" className="text-sm cursor-pointer">
            Notify customer via email
          </Label>
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
        <Button onClick={onSubmit} className="bg-blue-500 hover:bg-blue-600" data-testid="confirm-reschedule">
          <RefreshCw className="h-4 w-4 mr-1" />
          Save Changes
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
  );
};

export default RescheduleModal;
